// Which image the overlay's face layer draws on, and which boxes belong to it.
// Pure: the picking rule only, no fetching (ADR-0039).
import { isPdf } from '@/components/faces/format'
import type { DocumentFacePerson } from '@/services/api/photos'

export interface FaceLayer {
  /** The image URL the boxes are relative to — for a PDF, the page render. */
  sourceUrl: string
  /** Every face of that very image, whatever its state, the focused one included. */
  faces: DocumentFacePerson[]
  /** The page the layer draws, null for an image document. */
  page: number | null
  /** The face to emphasise, null when the overlay was opened without one. */
  focusedFaceId: string | null
}

/**
 * The layer for this document, or null when there is nothing to draw.
 *
 * A face's box is stored in 0–1 of *its own* source image, so only the faces of
 * one image may share a layer: an image document has a single one, a PDF has one
 * stored render per page (`.faces/page-<n>.jpg`).
 *
 * The page follows the analyst. *selectedFaceId* is the row or box they just
 * picked and it wins, so selecting a face of another page switches the preview to
 * that page (slice 06); *focusFaceId* is the face the overlay was opened on, which
 * is how the identity *Photos* tab points at its person (SC-005). A PDF with
 * neither keeps its normal preview, because no page of it is more relevant than
 * another.
 */
export function pickFaceLayer(
  faces: DocumentFacePerson[] | undefined,
  {
    focusFaceId,
    focusPage,
    selectedFaceId,
    fileType,
  }: {
    focusFaceId?: string | null
    /**
     * The page the focused face was opened on (slice 08). It anchors the layer
     * when the face itself is not among the document's faces any more — a
     * decision may have moved it — so a focused PDF still opens on that page.
     */
    focusPage?: number | null
    /** The face picked in the section or on the photo; it drives the page. */
    selectedFaceId?: string | null
    fileType?: string | null
  } = {}
): FaceLayer | null {
  const drawable = (faces ?? []).filter(face => face.source_url && face.box.length >= 4)
  if (drawable.length === 0) return null
  const wanted = [selectedFaceId, focusFaceId].filter(Boolean) as string[]
  const focused = wanted
    .map(faceId => drawable.find(face => face.face_id === faceId))
    .find(face => face !== undefined)
  const onFocusPage =
    focusPage === null || focusPage === undefined
      ? undefined
      : drawable.find(face => face.page === focusPage)
  if (!focused && !onFocusPage && isPdf(fileType)) return null
  const anchor = focused ?? onFocusPage ?? drawable[0]
  const sourceUrl = anchor.source_url as string
  return {
    sourceUrl,
    faces: drawable.filter(face => face.source_url === sourceUrl),
    page: anchor.page,
    focusedFaceId: focused?.face_id ?? null,
  }
}
